/**
 * ============================================================================
 * @altkey/i18n - Locale Detection, Persistence & Message Schema
 * ============================================================================
 * Questo modulo e' volutamente "framework-agnostic": non dipende da Vue, ne'
 * da alcun sistema di reattivita' esterno. Espone un piccolo store
 * osservabile (pattern publish/subscribe) che sia `apps/desktop` sia
 * `apps/mobile` possono avvolgere nella propria reattivita' nativa
 * (es. `ref()` di Vue 3) senza costringere questo package a dipendere
 * dal framework UI.
 *
 * Il modulo e' inoltre "environment-agnostic": non presuppone `lib: DOM`
 * (vedi tsconfig.json del package). Ogni accesso a `navigator`,
 * `localStorage` o `indexedDB` e' fatto tramite feature-detection su
 * `globalThis`, con tipi minimi locali, cosi' il file compila ed esegue in
 * sicurezza sia nel browser (desktop/mobile), sia in un service worker PWA
 * (dove `localStorage` NON esiste ma `indexedDB` si'), sia in ambienti senza
 * alcuna delle due API (es. test in Node puro, Workers backend).
 * ============================================================================
 */

/* ============================================================================
 * LOCALE CODE
 * ============================================================================
 * NOTA: duplicato intenzionalmente rispetto a `@altkey/database` per evitare
 * una dipendenza diretta tra i due package. Quando `@altkey/shared-types`
 * sara' completato, questo tipo verra' promosso li' come fonte di verita'
 * unica e importato da entrambi.
 * ============================================================================ */

export type LocaleCode = 'en' | 'it' | 'de' | 'fr' | 'es';

export const SUPPORTED_LOCALES: readonly LocaleCode[] = Object.freeze([
  'en',
  'it',
  'de',
  'fr',
  'es',
]);

export const DEFAULT_LOCALE: LocaleCode = 'en';

export function isSupportedLocaleCode(value: unknown): value is LocaleCode {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

/* ============================================================================
 * 1. RILEVAMENTO SINCRONO DELLA LINGUA DEL BROWSER
 * ============================================================================ */

/**
 * Sottoinsieme minimo dell'interfaccia `Navigator` di cui questo modulo ha
 * bisogno, dichiarato localmente per non richiedere `lib: "DOM"` nel tsconfig.
 */
interface MinimalNavigator {
  readonly language?: string;
  readonly languages?: readonly string[];
}

function getNavigatorSafe(): MinimalNavigator | null {
  const globalObj = globalThis as unknown as { navigator?: MinimalNavigator };
  return globalObj.navigator ?? null;
}

/**
 * Estrae il subtag di lingua primario da un tag BCP-47 (es. "it-IT" -> "it",
 * "en-US" -> "en", "de" -> "de") e lo confronta, case-insensitive, con i
 * locale supportati da AltKey.app.
 */
function normalizeToSupportedLocale(tag: string): LocaleCode | null {
  const primarySubtag = tag.split('-')[0]?.toLowerCase();
  if (!primarySubtag) return null;
  return isSupportedLocaleCode(primarySubtag) ? primarySubtag : null;
}

/**
 * Rileva in modo SINCRONO la lingua nativa del browser dell'utente,
 * ispezionando `navigator.languages` (ordine di preferenza dichiarato
 * dall'utente nel sistema operativo/browser) e, in fallback,
 * `navigator.language`. Se nessuna lingua rilevata e' tra quelle supportate
 * (EN, IT, DE, FR, ES), restituisce `DEFAULT_LOCALE` ("en").
 *
 * Sincrono per costruzione: nessuna Promise, nessun `await`, cosi' puo'
 * essere chiamato durante il bootstrap dell'app senza causare un flash di
 * contenuto nella lingua sbagliata.
 */
export function detectBrowserLocale(): LocaleCode {
  const nav = getNavigatorSafe();
  if (!nav) return DEFAULT_LOCALE;

  if (Array.isArray(nav.languages)) {
    for (const tag of nav.languages) {
      const match = normalizeToSupportedLocale(tag);
      if (match) return match;
    }
  }

  if (typeof nav.language === 'string') {
    const match = normalizeToSupportedLocale(nav.language);
    if (match) return match;
  }

  return DEFAULT_LOCALE;
}

/* ============================================================================
 * 2. PERSISTENZA DELLA PREFERENZA (localStorage sincrono + IndexedDB async)
 * ============================================================================
 * localStorage e' la fonte di verita' primaria: e' sincrona, quindi la
 * preferenza salvata puo' essere letta immediatamente al bootstrap, prima
 * ancora che l'app finisca di montarsi, evitando il flash-of-wrong-locale.
 *
 * IndexedDB viene scritto in parallelo (fire-and-forget, mai bloccante) come
 * layer di persistenza secondario: e' l'unico storage disponibile in un
 * Service Worker PWA (dove `localStorage` non esiste), quindi garantisce che
 * la preferenza lingua sia recuperabile anche da quel contesto (es. per
 * localizzare le notifiche push generate dal Service Worker di
 * `apps/mobile`).
 * ============================================================================ */

const LOCALE_STORAGE_KEY = 'altkey:locale-preference';
const IDB_DATABASE_NAME = 'altkey-i18n';
const IDB_DATABASE_VERSION = 1;
const IDB_STORE_NAME = 'preferences';
const IDB_LOCALE_KEY = 'locale';

interface MinimalStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function getLocalStorageSafe(): MinimalStorage | null {
  try {
    const globalObj = globalThis as unknown as { localStorage?: MinimalStorage };
    const storage = globalObj.localStorage;
    if (!storage) return null;
    // Alcuni browser in modalita' privata espongono `localStorage` ma
    // lanciano un'eccezione al primo accesso: verifichiamolo subito.
    const probeKey = '__altkey_probe__';
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return storage;
  } catch {
    return null;
  }
}

/**
 * Legge in modo sincrono la preferenza di lingua salvata in precedenza,
 * se presente e valida. Restituisce `null` se non c'e' storage disponibile,
 * la chiave non e' presente, o il valore salvato non e' piu' un locale
 * supportato (es. dopo una rimozione di una lingua in una versione futura).
 */
export function getStoredLocalePreference(): LocaleCode | null {
  const storage = getLocalStorageSafe();
  if (!storage) return null;

  try {
    const value = storage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocaleCode(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Scrive la preferenza di lingua in localStorage (sincrono) e specchia la
 * stessa scrittura in IndexedDB in background (asincrono, non bloccante).
 * Entrambe le scritture sono "best effort": eventuali errori (storage pieno,
 * modalita' privata, API assente) vengono ignorati silenziosamente, perche'
 * la preferenza lingua non e' dato critico: nel caso peggiore l'utente dovra'
 * semplicemente riselezionare la lingua al prossimo avvio.
 */
export function setStoredLocalePreference(locale: LocaleCode): void {
  const storage = getLocalStorageSafe();
  if (storage) {
    try {
      storage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Scrittura best-effort: ignora silenziosamente (es. quota esaurita).
    }
  }

  void persistLocalePreferenceIndexedDB(locale);
}

/* ---- IndexedDB minimal typed helpers (nessuna dipendenza da lib DOM) ---- */

interface MinimalIDBRequest {
  result: unknown;
  error: unknown;
  onsuccess: (() => void) | null;
  onerror: (() => void) | null;
}

interface MinimalIDBOpenRequest extends MinimalIDBRequest {
  result: MinimalIDBDatabase;
  onupgradeneeded: (() => void) | null;
}

interface MinimalIDBObjectStore {
  put(value: unknown, key: string): MinimalIDBRequest;
  get(key: string): MinimalIDBRequest;
}

interface MinimalIDBTransaction {
  objectStore(name: string): MinimalIDBObjectStore;
}

interface MinimalIDBDatabase {
  objectStoreNames: { contains(name: string): boolean };
  createObjectStore(name: string): void;
  transaction(storeNames: string, mode: 'readonly' | 'readwrite'): MinimalIDBTransaction;
  close(): void;
}

interface MinimalIDBFactory {
  open(name: string, version: number): MinimalIDBOpenRequest;
}

function getIndexedDbSafe(): MinimalIDBFactory | null {
  const globalObj = globalThis as unknown as { indexedDB?: MinimalIDBFactory };
  return globalObj.indexedDB ?? null;
}

function openLocaleDatabase(): Promise<MinimalIDBDatabase | null> {
  return new Promise((resolve) => {
    const idb = getIndexedDbSafe();
    if (!idb) {
      resolve(null);
      return;
    }

    try {
      const request = idb.open(IDB_DATABASE_NAME, IDB_DATABASE_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
          db.createObjectStore(IDB_STORE_NAME);
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Specchia la preferenza di lingua in IndexedDB. Fire-and-forget: gli
 * eventuali errori sono contenuti e non propagati, dato che localStorage
 * resta la fonte di verita' primaria per i contesti in cui e' disponibile.
 */
export async function persistLocalePreferenceIndexedDB(locale: LocaleCode): Promise<void> {
  try {
    const db = await openLocaleDatabase();
    if (!db) return;

    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readwrite');
      const store = tx.objectStore(IDB_STORE_NAME);
      const request = store.put(locale, IDB_LOCALE_KEY);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
    });

    db.close();
  } catch {
    // Persistenza best-effort: nessuna propagazione dell'errore.
  }
}

/**
 * Legge la preferenza di lingua da IndexedDB. Utile nei contesti dove
 * `localStorage` non e' disponibile (es. Service Worker di `apps/mobile`).
 * Asincrona per natura: non utilizzabile per il bootstrap sincrono iniziale.
 */
export async function readLocalePreferenceIndexedDB(): Promise<LocaleCode | null> {
  try {
    const db = await openLocaleDatabase();
    if (!db) return null;

    const result = await new Promise<unknown>((resolve) => {
      const tx = db.transaction(IDB_STORE_NAME, 'readonly');
      const store = tx.objectStore(IDB_STORE_NAME);
      const request = store.get(IDB_LOCALE_KEY);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });

    db.close();
    return isSupportedLocaleCode(result) ? result : null;
  } catch {
    return null;
  }
}

/**
 * Risolve la lingua iniziale da usare al bootstrap dell'app, con priorita':
 *   1. Preferenza esplicita salvata dall'utente (localStorage, sincrono)
 *   2. Lingua nativa rilevata dal browser
 *   3. DEFAULT_LOCALE ("en") come ultima rete di sicurezza
 */
export function resolveInitialLocale(): LocaleCode {
  return getStoredLocalePreference() ?? detectBrowserLocale();
}

/* ============================================================================
 * STATO REATTIVO DELLA LINGUA ATTIVA (framework-agnostic observable)
 * ============================================================================ */

type LocaleChangeListener = (locale: LocaleCode) => void;

class LocaleStore {
  private current: LocaleCode;
  private readonly listeners: Set<LocaleChangeListener> = new Set();

  constructor(initial: LocaleCode) {
    this.current = initial;
  }

  /** Restituisce la lingua attualmente attiva. */
  get(): LocaleCode {
    return this.current;
  }

  /**
   * Imposta manualmente la lingua attiva, persiste la preferenza (localStorage
   * sincrono + IndexedDB async) e notifica tutti i listener registrati.
   * No-op se la lingua richiesta e' gia' quella attiva.
   */
  set(locale: LocaleCode): void {
    if (locale === this.current) return;

    this.current = locale;
    setStoredLocalePreference(locale);

    for (const listener of this.listeners) {
      listener(locale);
    }
  }

  /**
   * Registra un listener invocato ad ogni cambio di lingua. Restituisce una
   * funzione di unsubscribe, da chiamare per evitare memory leak quando il
   * componente consumer (es. un componente Vue) viene smontato.
   */
  subscribe(listener: LocaleChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

/**
 * Istanza singleton dello store di lingua, inizializzata risolvendo subito
 * (in modo sincrono) la lingua iniziale secondo le priorita' descritte in
 * `resolveInitialLocale`.
 */
export const localeStore = new LocaleStore(resolveInitialLocale());

/** Restituisce la lingua attiva corrente. Scorciatoia su `localeStore.get()`. */
export function getActiveLocale(): LocaleCode {
  return localeStore.get();
}

/** Imposta manualmente la lingua attiva. Scorciatoia su `localeStore.set()`. */
export function setActiveLocale(locale: LocaleCode): void {
  localeStore.set(locale);
}

/**
 * Sottoscrive un listener ai cambi di lingua. Scorciatoia su
 * `localeStore.subscribe()`. Restituisce la funzione di unsubscribe.
 */
export function onLocaleChange(listener: LocaleChangeListener): () => void {
  return localeStore.subscribe(listener);
}

/* ============================================================================
 * 3. MESSAGE SCHEMA
 * ============================================================================
 * Schema rigoroso e obbligatorio dei messaggi tradotti. Ogni catalogo di
 * lingua in `packages/i18n/src/locales/*.ts` dovra' implementare
 * `MessageSchema` per intero: TypeScript segnalera' in fase di build
 * qualunque chiave mancante o extra in una qualsiasi delle 5 lingue,
 * garantendo sincronizzazione al 100% tra i cataloghi.
 * ============================================================================ */

export interface MessageSchema {
  readonly common: {
    readonly appName: string;
    readonly ok: string;
    readonly cancel: string;
    readonly confirm: string;
    readonly loading: string;
    readonly retry: string;
    readonly close: string;
    readonly copy: string;
    readonly copied: string;
  };
  readonly pairing: {
    readonly title: string;
    readonly scanQrPrompt: string;
    readonly enterCodeManually: string;
    readonly waitingForDesktop: string;
    readonly waitingForMobile: string;
    readonly connected: string;
    readonly disconnected: string;
    readonly expired: string;
    readonly invalidCode: string;
    readonly keysExchanged: string;
    readonly scanDevice: string;
    readonly showMyCode: string;
    readonly hostingPrompt: string;
    readonly goBack: string;
    readonly disconnectAction: string;
    readonly deviceConnected: string;
    readonly backToCategories: string;
  };
  readonly license: {
    readonly proUnlockedPromo: string;
    readonly proUnlockedLicense: string;
    readonly freeTierPrompt: string;
    readonly invalidLicense: string;
    readonly enterLicenseKey: string;
  };
  readonly search: {
    readonly placeholder: string;
    readonly noResults: string;
    readonly resultsCount: string;
    readonly filterByCategory: string;
    readonly recentSymbols: string;
  };
  readonly tofuDetector: {
    readonly title: string;
    readonly scanning: string;
    readonly issuesFound: string;
    readonly noIssuesFound: string;
    readonly replaceSuggestion: string;
  };
  readonly macroGenerator: {
    readonly title: string;
    readonly recordMacro: string;
    readonly stopRecording: string;
    readonly saveMacro: string;
    readonly macroSaved: string;
    readonly deleteMacro: string;
  };
  readonly aiCanvasInput: {
    readonly title: string;
    readonly drawPrompt: string;
    readonly recognizing: string;
    readonly recognitionFailed: string;
    readonly insertSymbol: string;
  };
  readonly settings: {
    readonly title: string;
    readonly language: string;
    readonly theme: string;
    readonly themeLight: string;
    readonly themeDark: string;
    readonly themeSystem: string;
    readonly startOnBoot: string;
    readonly notifications: string;
    readonly about: string;
    readonly version: string;
    readonly checkForUpdates: string;
    readonly signOut: string;
  };
  readonly home: {
    readonly title: string;
    readonly greeting: string;
    readonly pairWithDesktop: string;
    readonly recentActivity: string;
    readonly quickActions: string;
    readonly noRecentActivity: string;
  };
  readonly errors: {
    readonly generic: string;
    readonly network: string;
    readonly sessionExpired: string;
    readonly unauthorized: string;
    readonly notFound: string;
  };
}

/**
 * Utility type che genera l'unione di tutti i percorsi "dot-path" delle
 * foglie stringa di `MessageSchema` (es. "common.ok" | "pairing.title" | ...).
 * Verra' usata dalla futura funzione `t(key: MessageKey, ...)` del layer di
 * traduzione per garantire, anche lato chiamante, che si possa richiedere
 * solo una chiave che esiste davvero nello schema.
 */
export type DeepMessageKeyOf<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends Record<string, unknown>
      ? `${K}.${DeepMessageKeyOf<T[K]>}`
      : never;
}[keyof T & string];

export type MessageKey = DeepMessageKeyOf<MessageSchema>;

/**
 * Struttura completa di un catalogo di traduzione per una singola lingua:
 * il locale a cui appartiene + l'intero set di messaggi conforme allo schema.
 */
export interface LocaleCatalog {
  readonly locale: LocaleCode;
  readonly messages: MessageSchema;
}